import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";

/**
 * Privacy Policy page — deliberately dense with the concrete facts a GDPR
 * user needs (categories of data, legal basis, retention, contact) without
 * legalese fluff. If you retain a lawyer to draft a bespoke policy, replace
 * the body copy but keep the same section anchors so the cookie banner deep-
 * links keep working.
 */
export default function Privacy() {
  const { t } = useLang();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="privacy-page">
      <div className="glass-header sticky top-0 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 h-20">
          <button onClick={() => nav(-1)} className="btn-ghost focus-ring" data-testid="privacy-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[11px] uppercase tracking-[0.15em]">{t("profile.back")}</span>
          </button>
          <div className="label-eyebrow">{t("privacy.eyebrow")}</div>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-14 space-y-10" data-testid="privacy-content">
        <header>
          <div className="label-eyebrow mb-3">{t("privacy.eyebrow")}</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95] mb-3">
            {t("privacy.title")}
          </h1>
          <p className="text-white/50 text-[13px] font-mono-p">{t("privacy.updated")}</p>
        </header>

        <Section id="overview" title={t("privacy.s1.title")}>
          <p>{t("privacy.s1.body")}</p>
        </Section>

        <Section id="data-we-collect" title={t("privacy.s2.title")}>
          <ul className="space-y-2">
            <li><strong className="text-white">{t("privacy.s2.li1.k")}</strong> — {t("privacy.s2.li1.v")}</li>
            <li><strong className="text-white">{t("privacy.s2.li2.k")}</strong> — {t("privacy.s2.li2.v")}</li>
            <li><strong className="text-white">{t("privacy.s2.li3.k")}</strong> — {t("privacy.s2.li3.v")}</li>
            <li><strong className="text-white">{t("privacy.s2.li4.k")}</strong> — {t("privacy.s2.li4.v")}</li>
          </ul>
        </Section>

        <Section id="cookies" title={t("privacy.s3.title")}>
          <p>{t("privacy.s3.body")}</p>
          <ul className="space-y-2 mt-3">
            <li><strong className="text-white">access_token / session_token</strong> — {t("privacy.s3.li1")}</li>
            <li><strong className="text-white">pq_token / pq_lang / pq_cookie_consent</strong> — {t("privacy.s3.li2")}</li>
          </ul>
        </Section>

        <Section id="third-parties" title={t("privacy.s4.title")}>
          <p>{t("privacy.s4.body")}</p>
          <ul className="space-y-2 mt-3">
            <li><strong className="text-white">OpenRouter</strong> — {t("privacy.s4.li1")}</li>
            <li><strong className="text-white">Polar.sh</strong> — {t("privacy.s4.li2")}</li>
            <li><strong className="text-white">OpenAI (Whisper / TTS)</strong> — {t("privacy.s4.li3")}</li>
            <li><strong className="text-white">Google (OAuth)</strong> — {t("privacy.s4.li4")}</li>
          </ul>
        </Section>

        <Section id="legal-basis" title={t("privacy.s5.title")}>
          <p>{t("privacy.s5.body")}</p>
        </Section>

        <Section id="retention" title={t("privacy.s6.title")}>
          <p>{t("privacy.s6.body")}</p>
        </Section>

        <Section id="rights" title={t("privacy.s7.title")}>
          <p>{t("privacy.s7.body")}</p>
        </Section>

        <Section id="contact" title={t("privacy.s8.title")}>
          <p>{t("privacy.s8.body")}</p>
        </Section>
      </article>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id}>
      <h2 className="font-display text-xl font-black tracking-tight mb-3 text-white/95">
        {title}
      </h2>
      <div className="text-[14px] text-white/70 leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );
}
